import React from "react";
import { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams, Navigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { useOnboardingSettings, AdAccountSettings } from "@/hooks/useOnboardingSettings";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingStepper } from "@/components/onboarding/OnboardingStepper";
import { AdProfilesStep } from "@/components/onboarding/steps/AdProfilesStep";
import { TrackingStep } from "@/components/onboarding/steps/TrackingStep";
import { NamingConventionStep } from "@/components/onboarding/steps/NamingConventionStep";
import { LaunchSettingsStep } from "@/components/onboarding/steps/LaunchSettingsStep";
import { CreativeEnhancementsStep } from "@/components/onboarding/steps/CreativeEnhancementsStep";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SetupAccount() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { setSelectedAdAccount } = useAdAccount();
  const { triggerBackgroundSync } = useBackgroundSync();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<Partial<AdAccountSettings>>({});

  // Verify account belongs to user
  const {
    data: account,
    isLoading: accountLoading,
    error: accountError,
  } = useQuery({
    queryKey: ["setup-account-verify", user?.id, accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ad_accounts")
        .select("id, ad_account_id, ad_account_name, setup_status")
        .eq("user_id", user!.id)
        .eq("ad_account_id", accountId!)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Account not found");
      return data;
    },
    enabled: !!user?.id && !!accountId,
    staleTime: 60 * 1000,
  });

  // Onboarding settings for this account
  const {
    settings,
    isLoading: settingsLoading,
    saveStep,
    completeOnboarding,
    isSaving,
  } = useOnboardingSettings(accountId ?? undefined);

  // Seed form data from loaded settings
  useEffect(() => {
    if (settings) setFormData(settings);
  }, [settings]);

  // On mount: mark in_progress & trigger sync
  useEffect(() => {
    if (!account || !user?.id || !accountId) return;

    if (account.setup_status !== "completed") {
      supabase
        .from("ad_accounts")
        .update({ setup_status: "in_progress" as any })
        .eq("user_id", user.id)
        .eq("ad_account_id", accountId)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["ad-account-row"] });
          queryClient.invalidateQueries({ queryKey: ["unconfigured-accounts"] });
        });
    }

    triggerBackgroundSync(user.id, accountId);
  }, [account, user?.id, accountId]);

  const canContinue = useCallback(() => {
    // All fields are optional — users can skip every step
    return true;
  }, []);

  const handleContinue = async () => {
    try {
      await saveStep(formData);
      setCompletedSteps((prev) => new Set([...prev, currentStep]));

      if (currentStep === 5) {
        await completeOnboarding();

        // Mark completed
        await supabase
          .from("ad_accounts")
          .update({ setup_status: "completed" as any })
          .eq("user_id", user!.id)
          .eq("ad_account_id", accountId!);

        queryClient.invalidateQueries({ queryKey: ["ad-account-row"] });
        queryClient.invalidateQueries({ queryKey: ["unconfigured-accounts"] });
        queryClient.invalidateQueries({ queryKey: ["ad-account-list"] });
        queryClient.invalidateQueries({ queryKey: ["ad-account-statuses"] });

        // Set as active account
        setSelectedAdAccount(accountId!, account?.ad_account_name ?? null);

        toast.success("Account configured successfully");
        setShowSuccess(true);
        setTimeout(() => navigate("/launcher/ad-sheets"), 1500);
      } else {
        setCurrentStep((s) => s + 1);
      }
    } catch (err) {
      toast.error("Failed to save. Please try again.");
      console.error(err);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleStepClick = (step: number) => {
    if (completedSteps.has(step)) setCurrentStep(step);
  };

  const updateFormData = (partial: Partial<AdAccountSettings>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  // Auth guard
  if (!authLoading && !user) return <Navigate to="/auth" replace />;

  if (authLoading || accountLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (accountError || !account) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-sm text-muted-foreground">Account not found or you don't have access.</p>
        <Button variant="outline" onClick={() => navigate("/settings")}>
          Back to Settings
        </Button>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <CheckCircle2 className="h-12 w-12 text-green-500" />
        <h2 className="text-xl font-semibold text-foreground">Setup complete!</h2>
        <p className="text-sm text-muted-foreground">Redirecting you now...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div>
        <Link
          to="/settings"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Set up {account.ad_account_name}</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure defaults for this ad account.</p>
      </div>

      {/* Wizard */}
      <div className="max-w-4xl">
        {/* Mobile stepper */}
        <div className="md:hidden mb-6">
          <OnboardingStepper currentStep={currentStep} completedSteps={completedSteps} onStepClick={handleStepClick} />
        </div>

        <div className="flex gap-8">
          {/* Desktop stepper */}
          <div className="hidden md:block">
            <OnboardingStepper
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={handleStepClick}
            />
          </div>

          {/* Step content */}
          <div className="flex-1 max-w-[640px]">
            {currentStep === 1 && accountId && (
              <AdProfilesStep
                adAccountId={accountId}
                fbPageId={formData.default_fb_page_id ?? null}
                fbPageName={formData.default_fb_page_name ?? null}
                igAccountId={formData.default_ig_account_id ?? null}
                igAccountName={formData.default_ig_account_name ?? null}
                onChange={updateFormData}
              />
            )}

            {currentStep === 2 && (
              <TrackingStep
                data={{
                  default_pixel_id: formData.default_pixel_id ?? null,
                  default_pixel_name: formData.default_pixel_name ?? null,
                  default_app_id: formData.default_app_id ?? null,
                  default_app_name: formData.default_app_name ?? null,
                  website_events_enabled: formData.website_events_enabled ?? false,
                  app_events_enabled: formData.app_events_enabled ?? false,
                  offline_events_enabled: formData.offline_events_enabled ?? false,
                  crm_events_enabled: formData.crm_events_enabled ?? false,
                  post_engagements_enabled: formData.post_engagements_enabled ?? false,
                  page_engagements_enabled: formData.page_engagements_enabled ?? false,
                  default_utm_parameters: (formData as any).default_utm_parameters ?? "",
                }}
                onChange={updateFormData}
                adAccountId={accountId ?? undefined}
              />
            )}

            {currentStep === 3 && (
              <NamingConventionStep
                data={{
                  naming_convention:
                    (formData.naming_convention as { tags: string[]; separator: string } | null) ?? null,
                }}
                onChange={updateFormData}
              />
            )}

            {currentStep === 4 && (
              <LaunchSettingsStep
                data={{
                  multi_advertiser_opt_in: formData.multi_advertiser_opt_in ?? true,
                  default_ad_status: formData.default_ad_status ?? "PAUSED",
                }}
                onChange={updateFormData}
              />
            )}

            {currentStep === 5 && (
              <CreativeEnhancementsStep
                data={{
                  image_enhancements: (formData.image_enhancements as Record<string, boolean> | null) ?? null,
                  video_enhancements: (formData.video_enhancements as Record<string, boolean> | null) ?? null,
                  carousel_enhancements: (formData.carousel_enhancements as Record<string, boolean> | null) ?? null,
                  collection_enhancements: (formData.collection_enhancements as Record<string, "OPT_IN" | "OPT_OUT"> | null) ?? null,
                  enhancements_catalog: (formData.enhancements_catalog as Record<string, boolean> | null) ?? null,
                  flexible_advantage_plus_creative: formData.flexible_advantage_plus_creative ?? false,
                  sitelinks: Array.isArray(formData.sitelinks) ? formData.sitelinks as any : [],
                }}
                onChange={updateFormData}
              />
            )}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-6">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={handleBack} disabled={isSaving}>
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              <Button onClick={handleContinue} disabled={!canContinue() || isSaving}>
                {isSaving ? "Saving..." : currentStep === 5 ? "Finish Setup" : "Continue"}
                {!isSaving && currentStep < 5 && <ArrowRight className="h-4 w-4 ml-1.5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
